import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Layout } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { useBusinesses } from "@/hooks/use-businesses";
import { apiRequest } from "@/lib/queryClient";
import { Plus, Send, Trash2, FileText, Loader2, AlertTriangle, CheckCircle } from "lucide-react";

const fmt = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD" });

interface Line { account_id: string; debit: string; credit: string; }

export default function JournalEntriesPage() {
  const { activeBusiness } = useBusinesses();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const businessId = activeBusiness?.id;

  const [showCreate, setShowCreate] = useState(false);
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [memo, setMemo] = useState("");
  const [lines, setLines] = useState<Line[]>([{ account_id: "", debit: "", credit: "" }, { account_id: "", debit: "", credit: "" }]);

  // Fetch entries
  const { data: entries = [], isLoading } = useQuery({
    queryKey: ["/api/businesses", businessId, "journal-entries"],
    queryFn: async () => { const r = await apiRequest("GET", `/api/businesses/${businessId}/journal-entries`); return r.json(); },
    enabled: !!businessId,
  });

  // Fetch COA
  const { data: coaData } = useQuery<{ accounts: any[] }>({
    queryKey: ["/api/businesses", businessId, "chart-of-accounts"],
    queryFn: async () => { const r = await apiRequest("GET", `/api/businesses/${businessId}/chart-of-accounts`); return r.json(); },
    enabled: !!businessId,
  });
  const accounts = coaData?.accounts || [];

  // Create
  const createMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        date, memo,
        lines: lines.filter(l => l.account_id).map(l => ({
          account_id: parseInt(l.account_id),
          debit: parseFloat(l.debit) || 0,
          credit: parseFloat(l.credit) || 0,
        })),
      };
      const r = await apiRequest("POST", `/api/businesses/${businessId}/journal-entries`, payload);
      return r.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/businesses", businessId, "journal-entries"] });
      toast({ title: "Journal entry created" });
      resetForm();
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  // Post
  const postMutation = useMutation({
    mutationFn: async (id: number) => {
      const r = await apiRequest("POST", `/api/businesses/${businessId}/journal-entries/${id}/post`);
      return r.json();
    },
    onSuccess: (d) => {
      queryClient.invalidateQueries({ queryKey: ["/api/businesses", businessId, "journal-entries"] });
      toast({ title: "Posted", description: `${d.transactionsCreated} transactions created` });
    },
    onError: (e: any) => toast({ title: "Post failed", description: e.message, variant: "destructive" }),
  });

  // Delete
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => { await apiRequest("DELETE", `/api/businesses/${businessId}/journal-entries/${id}`); },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/businesses", businessId, "journal-entries"] });
      toast({ title: "Deleted" });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const resetForm = () => {
    setShowCreate(false);
    setDate(new Date().toISOString().split("T")[0]);
    setMemo("");
    setLines([{ account_id: "", debit: "", credit: "" }, { account_id: "", debit: "", credit: "" }]);
  };

  const addLine = () => setLines([...lines, { account_id: "", debit: "", credit: "" }]);
  const removeLine = (i: number) => { if (lines.length > 2) setLines(lines.filter((_, idx) => idx !== i)); };
  const updateLine = (i: number, field: keyof Line, val: string) => {
    const updated = [...lines];
    updated[i] = { ...updated[i], [field]: val };
    // Clear the opposite side
    if (field === "debit" && val) updated[i].credit = "";
    if (field === "credit" && val) updated[i].debit = "";
    setLines(updated);
  };

  const totalDr = lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0);
  const totalCr = lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
  const isBalanced = Math.abs(totalDr - totalCr) < 0.01;
  const hasValidLines = lines.filter(l => l.account_id && (parseFloat(l.debit) || parseFloat(l.credit))).length >= 2;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="h-6 w-6 text-primary" />
            <div>
              <h1 className="text-2xl font-bold">Journal Entries</h1>
              <p className="text-muted-foreground text-sm">Double-entry bookkeeping adjustments</p>
            </div>
          </div>
          <Button onClick={() => setShowCreate(true)} className="gap-2"><Plus className="h-4 w-4" /> New Entry</Button>
        </div>

        {/* List */}
        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-6 space-y-3">{[1, 2, 3].map(i => <Skeleton key={i} className="h-12" />)}</div>
            ) : entries.length === 0 ? (
              <div className="p-12 text-center text-muted-foreground">
                <FileText className="h-10 w-10 mx-auto mb-3 opacity-30" />
                <p>No journal entries yet.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">#</TableHead>
                    <TableHead className="w-28">Date</TableHead>
                    <TableHead>Memo</TableHead>
                    <TableHead className="w-28 text-right">Amount</TableHead>
                    <TableHead className="w-24">Status</TableHead>
                    <TableHead className="w-28">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map((je: any) => (
                    <TableRow key={je.id}>
                      <TableCell className="font-mono text-sm">{je.id}</TableCell>
                      <TableCell className="text-sm">{new Date(je.date).toLocaleDateString()}</TableCell>
                      <TableCell className="text-sm">{je.memo || "—"}</TableCell>
                      <TableCell className="text-right font-mono">{fmt(Number(je.total_amount) || 0)}</TableCell>
                      <TableCell>
                        <Badge className={je.status === "posted" ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}>
                          {je.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {je.status === "draft" && (
                            <>
                              <Button size="sm" variant="default" onClick={() => postMutation.mutate(je.id)} disabled={postMutation.isPending} className="gap-1">
                                <Send className="h-3 w-3" /> Post
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => deleteMutation.mutate(je.id)}>
                                <Trash2 className="h-3 w-3 text-destructive" />
                              </Button>
                            </>
                          )}
                          {je.status === "posted" && <CheckCircle className="h-4 w-4 text-green-500" />}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Create Modal */}
        <Dialog open={showCreate} onOpenChange={(v) => { if (!v) resetForm(); else setShowCreate(true); }}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>New Journal Entry</DialogTitle>
              <DialogDescription>Create a balanced double-entry adjustment.</DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1"><Label>Date</Label><Input type="date" value={date} onChange={e => setDate(e.target.value)} /></div>
                <div className="space-y-1"><Label>Memo</Label><Input value={memo} onChange={e => setMemo(e.target.value)} placeholder="e.g. Adjusting entry for prepaid rent" /></div>
              </div>

              {/* Lines table */}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Account</TableHead>
                    <TableHead className="w-32 text-right">Debit</TableHead>
                    <TableHead className="w-32 text-right">Credit</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lines.map((line, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <Select value={line.account_id} onValueChange={v => updateLine(i, "account_id", v)}>
                          <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                          <SelectContent>
                            {accounts.map((a: any) => <SelectItem key={a.id} value={a.id.toString()}>{a.code} — {a.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell><Input type="number" step="0.01" min="0" className="text-right font-mono" value={line.debit} onChange={e => updateLine(i, "debit", e.target.value)} placeholder="0.00" /></TableCell>
                      <TableCell><Input type="number" step="0.01" min="0" className="text-right font-mono" value={line.credit} onChange={e => updateLine(i, "credit", e.target.value)} placeholder="0.00" /></TableCell>
                      <TableCell>{lines.length > 2 && <Button variant="ghost" size="icon" onClick={() => removeLine(i)}><Trash2 className="h-3 w-3" /></Button>}</TableCell>
                    </TableRow>
                  ))}
                  {/* Totals */}
                  <TableRow className="bg-muted/50 font-bold border-t-2">
                    <TableCell className="text-right">Totals</TableCell>
                    <TableCell className="text-right font-mono">{fmt(totalDr)}</TableCell>
                    <TableCell className="text-right font-mono">{fmt(totalCr)}</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              <div className="flex items-center justify-between">
                <Button variant="outline" size="sm" onClick={addLine} className="gap-1"><Plus className="h-3 w-3" /> Add Line</Button>
                {!isBalanced && totalDr + totalCr > 0 && (
                  <div className="flex items-center gap-1 text-sm text-red-600">
                    <AlertTriangle className="h-4 w-4" /> Difference: {fmt(Math.abs(totalDr - totalCr))}
                  </div>
                )}
                {isBalanced && totalDr > 0 && (
                  <div className="flex items-center gap-1 text-sm text-green-600">
                    <CheckCircle className="h-4 w-4" /> Balanced
                  </div>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={resetForm}>Cancel</Button>
              <Button onClick={() => createMutation.mutate()} disabled={!isBalanced || !hasValidLines || createMutation.isPending} className="gap-2">
                {createMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Save as Draft
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
